import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import PlatformRetailersModelMC from 'presentation/models/PlatformRetailersModelMC';
import ConversationModelMC from 'presentation/models/inbox/ConversationModelMC';
import {
  autoSelectIntegrationId,
  boundConversationIntegration,
  chatIdForMsSend,
  filterActiveWhatsappIntegrations,
  isMsQrPlatform
} from 'services/inbox/resolveNewChatMsIntegration';
import { resolveTemplateCustomerId } from 'services/inbox/resolveOutboundRecipient';
import useSelectedConversation from 'presentation/hooks/shared/useSelectedConversation';

const useNewChatMsIntegration = (currentCustomer, options = {}) => {
  const enabled = options.enabled !== false;
  const retailerInfo = useSelector((state) => state.retailerUsersReducer.retailer_info);
  const conversation = useSelectedConversation();
  const isMsWhatsapp = enabled
    && retailerInfo?.inbox_microservice_platforms?.whatsapp_messages === true;
  const customerId = resolveTemplateCustomerId(currentCustomer, conversation);
  const boundIntegration = useMemo(
    () => (isMsWhatsapp ? boundConversationIntegration(conversation, customerId) : null),
    [isMsWhatsapp, conversation, customerId]
  );

  const [integrations, setIntegrations] = useState([]);
  const [customerChats, setCustomerChats] = useState([]);
  const [selectedIntegrationId, setSelectedIntegrationId] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isMsWhatsapp || !retailerInfo?.id || boundIntegration) {
      setIntegrations(boundIntegration ? [boundIntegration] : []);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    PlatformRetailersModelMC.getWhatsappIntegrations(retailerInfo.id)
      .then((response) => {
        if (cancelled) return;
        setIntegrations(filterActiveWhatsappIntegrations(response));
      })
      .catch(() => {
        if (!cancelled) setIntegrations([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isMsWhatsapp, retailerInfo?.id, boundIntegration]);

  useEffect(() => {
    if (!isMsWhatsapp || !customerId) {
      setCustomerChats([]);
      return undefined;
    }

    let cancelled = false;
    ConversationModelMC.getMicroserviceCustomerChats({ customerId })
      .then((response) => {
        if (cancelled) return;
        setCustomerChats(response?.data || []);
      })
      .catch(() => {
        if (!cancelled) setCustomerChats([]);
      });

    return () => {
      cancelled = true;
    };
  }, [isMsWhatsapp, customerId]);

  useEffect(() => {
    if (boundIntegration) {
      setSelectedIntegrationId(boundIntegration.id);
      return;
    }
    setSelectedIntegrationId((current) => {
      if (current && integrations.some((integration) => String(integration.id) === String(current))) {
        return current;
      }
      return autoSelectIntegrationId(integrations, customerChats);
    });
  }, [boundIntegration, integrations, customerChats]);

  const selectedIntegration = useMemo(() => {
    if (boundIntegration) return boundIntegration;
    return integrations.find((integration) => String(integration.id) === String(selectedIntegrationId))
      || null;
  }, [boundIntegration, integrations, selectedIntegrationId]);

  const chatId = useMemo(() => chatIdForMsSend({
    conversation,
    customerId,
    customerChats,
    integrationId: selectedIntegration?.id
  }), [conversation, customerId, customerChats, selectedIntegration?.id]);

  const selectIntegration = useCallback((integrationId) => {
    setSelectedIntegrationId(integrationId || null);
  }, []);

  const isQrSelected = isMsQrPlatform(selectedIntegration?.platform);

  return useMemo(() => ({
    isMsWhatsapp,
    showSelector: isMsWhatsapp && !boundIntegration,
    integrations,
    selectedIntegration,
    selectIntegration,
    chatId,
    loading,
    isQrSelected
  }), [
    isMsWhatsapp,
    boundIntegration,
    integrations,
    selectedIntegration,
    selectIntegration,
    chatId,
    loading,
    isQrSelected
  ]);
};

export default useNewChatMsIntegration;
